/**
 * Hooks de rollback con TOTP (ADR_012 PR-CMgmt-G).
 *
 * useRollbackDevice / useRollbackProfile: mutaciones que envian el codigo
 * TOTP y devuelven la entry de auditoria. Invalidan queries relacionadas
 * (perfil, devices, historial) para que la UI refresque solita.
 *
 * useRollbackHistory: lista paginada del audit log con filtros opcionales.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  RollbackAuditList,
  RollbackDeviceRequest,
  RollbackDeviceResponse,
  RollbackProfileRequest,
  RollbackProfileResponse,
  RollbackStatus,
} from "@/api/types";

// ── Rollback individual ──────────────────────────────────────────────────

export function useRollbackDevice(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RollbackDeviceRequest) =>
      api<RollbackDeviceResponse>(
        `/api/v1/config/devices/${deviceId}/rollback`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["config", "device", deviceId] });
      qc.invalidateQueries({ queryKey: ["config", "rollbacks"] });
    },
  });
}

// ── Rollback masivo ──────────────────────────────────────────────────────

export function useRollbackProfile(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RollbackProfileRequest) =>
      api<RollbackProfileResponse>(
        `/api/v1/config/profiles/${profileId}/rollback`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["config", "profile", profileId] });
      qc.invalidateQueries({ queryKey: ["config", "rollbacks"] });
    },
  });
}

// ── Historial ────────────────────────────────────────────────────────────

interface UseRollbackHistoryParams {
  deviceId?: string;
  profileId?: string;
  status?: RollbackStatus;
  page?: number;
  perPage?: number;
}

export function useRollbackHistory(params: UseRollbackHistoryParams = {}) {
  const { deviceId, profileId, status, page = 1, perPage = 20 } = params;
  return useQuery({
    queryKey: [
      "config",
      "rollbacks",
      { deviceId, profileId, status, page, perPage },
    ],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams();
      if (deviceId) qs.set("device_id", deviceId);
      if (profileId) qs.set("profile_id", profileId);
      if (status) qs.set("status", status);
      qs.set("page", String(page));
      qs.set("per_page", String(perPage));
      return api<RollbackAuditList>(
        `/api/v1/config/rollbacks?${qs.toString()}`,
        { signal },
      );
    },
    staleTime: 30_000,
  });
}
